import { useState, useEffect, useRef } from 'react';
import { getAllTags } from '@/stores/notes$';
import { cn } from '@/lib/utils';

export default function TagEditAutocomplete({ initialValue, onSave, onCancel }) {
  const [input, setInput] = useState(initialValue);
  const [selectedIndex, setSelectedIndex] = useState(-1); // -1 means no selection, use typed value
  const inputRef = useRef(null);

  // Get all existing tags and filter based on input
  const allTags = getAllTags();
  const suggestions = input.trim()
    ? allTags
        .filter(tag => tag.toLowerCase().includes(input.toLowerCase()))
        .sort((a, b) => {
          // Prioritize tags that start with the input
          const aStarts = a.toLowerCase().startsWith(input.toLowerCase());
          const bStarts = b.toLowerCase().startsWith(input.toLowerCase());
          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;
          return a.localeCompare(b);
        })
        .slice(0, 10)
    : [];

  useEffect(() => {
    // Auto-focus input when component mounts
    inputRef.current?.focus();
    // Select all text for easy replacement
    inputRef.current?.select();
  }, []);

  const handleSave = () => {
    // Only use suggestion if user explicitly selected one with arrow keys
    if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
      onSave(suggestions[selectedIndex]);
    } else {
      // Use typed value
      onSave(input);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Start from -1 (no selection) or current selection
      setSelectedIndex(prev => {
        if (prev === -1) return 0; // First arrow down selects first item
        return Math.min(prev + 1, suggestions.length - 1);
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => {
        if (prev <= 0) return -1; // Arrow up from first item deselects
        return prev - 1;
      });
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  const handleSuggestionClick = (tag) => {
    onSave(tag);
  };

  return (
    <div className="relative inline-block">
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={(e) => {
          // Only close if clicking outside (not on suggestions)
          if (!e.relatedTarget?.closest('.tag-edit-suggestions')) {
            setTimeout(() => onCancel(), 150);
          }
        }}
        className="px-1.5 py-0.5 rounded text-xs border border-primary bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
        style={{ width: `${Math.max(input.length * 8 + 20, 60)}px` }}
      />

      {suggestions.length > 0 && (
        <div className="tag-edit-suggestions absolute top-full left-0 mt-1 w-48 bg-popover border border-border rounded-lg shadow-lg py-1 z-50 max-h-48 overflow-y-auto">
          {suggestions.map((tag, index) => (
            <button
              key={tag}
              onMouseDown={() => handleSuggestionClick(tag)}
              onMouseEnter={() => setSelectedIndex(index)}
              className={cn(
                "w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors",
                index === selectedIndex && selectedIndex >= 0 && "bg-accent"
              )}
            >
              #{tag}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
